"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useRef, useState } from "react";
import Button from "~/components/buttons/Button";
import DatePickerButton from "~/components/datePicker/DatePickerButton";
import Dropdown from "~/components/inputs/Dropdown";
import FileUpload from "~/components/FileUploader/FileUploader";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import { NumberInput } from "~/components/inputs/NumberInput";
import TextArea from "~/components/inputs/TextArea";
import Modal from "~/components/modal/Modal";
import Text from "~/components/texts/Text";
import { SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL } from "~/constants";
import { ROUTES_MAP } from "~/constants/routes";
import { useI18n } from "~/locales/client";
import { AvailabilityTypeEnum, ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import { SourceOfQuotationEnum, TypeOfStockEnum, UploadResponse } from "~/types-and-enums/pqIngestionTypes";
import { updateSearchParams } from "~/utils/clientUtils";
import { formatDateToYYYYMMDD } from "~/utils/dateUtils";
import { fileToBase64 } from "~/utils/generalUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import { createJob, uploadAttachment } from "../actions";
import { usePostHog } from "~/hooks/usePostHog";
import { useImpersonation } from "~/hooks/useImpersonation";

/**
 * PQ Ingestion File Upload Modal Component
 *
 * Allows users to upload price list files and submit them for processing
 */
const PQIngestionFileUploadModal = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const translate = useI18n();
  const { captureEvent } = usePostHog();
  const { isEmployee, effectiveUserEmail } = useImpersonation();
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [additionalInput, setAdditionalInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Type of stock state
  const [typeOfStock, setTypeOfStock] = useState<TypeOfStockEnum | "">("");

  // Available time period state
  const [pqType, setPqType] = useState<AvailabilityTypeEnum>(AvailabilityTypeEnum.ALWAYS_IN_STOCK);
  const [deliveryTimeInDays, setDeliveryTimeInDays] = useState("");
  const [availableFromDate, setAvailableFromDate] = useState<Date | null>(null);
  // Remembers the user-entered date for UPCOMING_STOCK so it survives type switches
  const upcomingStockDateRef = useRef<Date | null>(null);

  const shouldOpen = searchParams.get(SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL) === "true";

  const handleClose = (): void => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL);
    updateSearchParams(params);
    // Reset state
    setUploadResponse(null);
    setAdditionalInput("");
    setError(null);
    setUploadedFile(null);
    setTypeOfStock("");
    setPqType(AvailabilityTypeEnum.ALWAYS_IN_STOCK);
    setDeliveryTimeInDays("");
    setAvailableFromDate(null);
    upcomingStockDateRef.current = null;
  };

  const formatApiErrors = (errors?: Record<string, string[]>) => {
    if (!errors) return "";
    return Object.entries(errors)
      .map(([field, messages]) => {
        // Handle both array and string error messages
        const messageArray = Array.isArray(messages) ? messages : [messages];
        return `${field}: ${messageArray.join(", ")}`;
      })
      .join(" | ");
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    setError(null);
    setIsUploading(true);
    setUploadedFile(file);

    try {
      // Convert file to base64
      const base64Data = await fileToBase64(file);

      // Create payload with base64 encoded file
      const payload = {
        file: base64Data,
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      };

      // Call server action
      const result = await uploadAttachment(payload);

      if (result.success && result.data) {
        setUploadResponse(result.data);
      } else {
        const errorDetails = formatApiErrors(result.errors);
        const errorMessage = result.error || translate("general.error-occurred-while-uploading-files");
        setError(errorDetails ? `${errorMessage} (${errorDetails})` : errorMessage);
        setUploadedFile(null);
      }
    } catch (err) {
      setError(translate("general.error-occurred-while-uploading-files"));
      console.error("File upload error:", err);
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteUploadedFile = () => {
    setUploadResponse(null);
    setUploadedFile(null);
    setError(null);
  };

  /** Parses the delivery time input — returns 0 when empty or invalid. */
  const getDeliveryTimeValue = (): number => {
    const n = parseInt(deliveryTimeInDays, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const handleSubmitForProcessing = async () => {
    if (!uploadResponse) return;

    // Validate type of stock
    if (!typeOfStock) {
      setError(translate("pq-ingestion.type-of-stock-required"));
      return;
    }

    // Validate that additionalInput is not empty
    const trimmedInput = additionalInput.trim();
    if (!trimmedInput || trimmedInput.length === 0) {
      setError(translate("pq-ingestion.instructions-required") || "Instructions are required. Please provide additional input.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const source = isEmployee
      ? SourceOfQuotationEnum.SALESFORCE
      : SourceOfQuotationEnum.SUPPLIER_APP;
    const user = effectiveUserEmail ?? "";

    try {
      // Build earliest_availability_date based on pq_type
      let earliestAvailabilityDate: string | undefined;
      if (pqType === AvailabilityTypeEnum.IN_STOCK_NOW) {
        earliestAvailabilityDate = formatDateToYYYYMMDD(new Date());
      } else if (pqType === AvailabilityTypeEnum.UPCOMING_STOCK && availableFromDate) {
        earliestAvailabilityDate = formatDateToYYYYMMDD(availableFromDate);
      }

      const payload = {
        s3_key: uploadResponse.s3_key,
        original_filename: uploadResponse.original_filename,
        additional_input: trimmedInput,
        pq_type: pqType,
        type_of_stock: typeOfStock as TypeOfStockEnum,
        delivery_time_in_days: getDeliveryTimeValue(),
        source,
        user,
        ...(earliestAvailabilityDate && {
          earliest_availability_date: earliestAvailabilityDate,
        }),
      };

      // Call server action
      const result = await createJob(payload);

      if (result.success) {
        captureEvent("pq_file_submitted", {
          pq_type: pqType,
          type_of_stock: typeOfStock,
          source,
          user,
          file_name: uploadedFile?.name ?? null,
          file_size_kb: uploadedFile ? Math.round(uploadedFile.size / 1024) : null,
          file_type: uploadedFile?.type ?? null,
        });
        // Close modal
        handleClose();

        // Check if user is on pq-ingestion-files route
        if (pathname?.includes(ROUTES_MAP.pqIngestionFiles)) {
          // Reload the page if already on pq-ingestion-files
          window.location.reload();
        } else {
          // Navigate to pq-ingestion-files page
          const locale = pathname?.split("/")[1] || "en";
          router.push(`/${locale}${ROUTES_MAP.pqIngestionFiles}`);
        }
      } else {
        const errorDetails = formatApiErrors(result.errors);
        const errorMessage = result.error || "Failed to submit for processing. Please try again.";
        setError(errorDetails ? `${errorMessage} (${errorDetails})` : errorMessage);
      }
    } catch (err) {
      setError("Failed to submit for processing. Please try again.");
      console.error("Job submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldOpen) return null;

  return (
    <Modal
      isOpen={shouldOpen}
      closeModal={handleClose}
      outsideClick
      size="lg"
      modalContainerClassname="w-full max-w-[98vw] sm:max-w-[640px] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] flex flex-col"
      classname="p-6 pt-2 md:p-8 md:pt-2 lg:p-10 lg:pt-2 max-w-full! flex-1 flex flex-col overflow-hidden"
      childrenDivWrapperClassName="flex-1 flex flex-col overflow-hidden"
    >
      <Flex className="flex-col gap-6 w-full mx-auto flex-1 overflow-y-auto min-h-0">
        {/* Title */}
        <Flex className="flex-col gap-2 shrink-0">
          <Text className="text-2xl md:text-3xl font-bold text-gray-900">
            {translate("pq-ingestion.new-file-submission")}
          </Text>
          <Text className="text-sm text-gray-500">{translate("pq-ingestion.upload-supplier-price-list")}</Text>
        </Flex>

        {/* Error Message */}
        {error && (
          <Flex className="bg-red-50 border border-red-200 rounded-lg p-3 shrink-0">
            <Text className="text-sm text-red-700 wrap-break-word">{error}</Text>
          </Flex>
        )}

        {/* Step 1: Upload File */}
        <Flex className="flex-col gap-3 shrink-0">
          <Flex className="items-center gap-2">
            <Flex className="w-8 h-8 rounded-full bg-gray-900 items-center justify-center shrink-0">
              <Text className="text-white font-semibold text-sm">1</Text>
            </Flex>
            <Text className="text-base font-semibold text-gray-900 uppercase tracking-wide">
              {translate("pq-ingestion.upload-file")} <span>*</span>
            </Text>
          </Flex>

          {/* Container for smooth transitions */}
          <Flex className="relative w-full overflow-hidden">
            {/* FileUpload with fade transition */}
            <Flex
              className={`w-full transition-all duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${uploadResponse && uploadedFile
                ? "opacity-0 pointer-events-none scale-[0.97] translate-y-3 blur-sm absolute -z-10"
                : "opacity-100 pointer-events-auto scale-100 translate-y-0 blur-0 relative z-0"
                }`}
            >
              {(!uploadResponse || !uploadedFile) && (
                <FileUpload
                  onChange={handleFileUpload}
                  multiple={false}
                  acceptFileTypes=".png,.webp,.xlsx,.pdf,.csv,.jpeg,.jpg"
                  isDisabled={isUploading || !!(uploadResponse && uploadedFile)}
                  displayUploadedFiles={false}
                />
              )}
            </Flex>

            {/* Loading state overlay */}
            {isUploading && (
              <Flex className="absolute inset-0 items-center justify-center gap-2 bg-white bg-opacity-90 rounded-lg z-30 transition-opacity duration-300">
                <Icon name={IconName.Loader} size={spacing[8]} customStrokeWidth={2.5} className="animate-spin" color={colors.gray[600]} />
              </Flex>
            )}

            {/* Success message with fade transition */}
            <Flex
              className={`w-full transition-all duration-700 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${uploadResponse && uploadedFile
                ? "opacity-100 pointer-events-auto scale-100 translate-y-0 blur-0 relative z-10"
                : "opacity-0 pointer-events-none scale-[0.97] translate-y-3 blur-sm absolute -z-10"
                }`}
            >
              {uploadResponse && uploadedFile && (
                <Flex className="flex-col gap-2 p-2 bg-green-50 border border-green-200 rounded-lg w-full">
                  <Flex className="items-center justify-between gap-2">
                    <Flex className="items-center gap-2 flex-1 min-w-0">
                      <Icon name={IconName.CheckCircle} size={spacing[4]} color={colors.green[600]} />
                      <Text className="text-sm text-green-700 font-medium">
                        {translate("general.file-uploaded-successfully")}
                      </Text>
                    </Flex>
                    <Icon
                      name={IconName.Trash}
                      size={spacing[5]}
                      onClick={handleDeleteUploadedFile}
                      className="cursor-pointer hover:opacity-70 shrink-0 transition-opacity duration-200"
                      color={colors.red[600]}
                    />
                  </Flex>
                  <Text className="text-xs text-gray-600 ml-6 truncate">
                    {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(2)} KB)
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>

        {/* Step 2: Type of Stock */}
        <Flex className="flex-col gap-3 shrink-0">
          <Flex className="items-center gap-2">
            <Flex className="w-8 h-8 rounded-full bg-gray-300 items-center justify-center shrink-0">
              <Text className="text-gray-600 font-semibold text-sm">2</Text>
            </Flex>
            <Text className="text-base font-semibold text-gray-900 uppercase tracking-wide">
              {translate("pq-ingestion.type-of-stock")} <span>*</span>
            </Text>
          </Flex>

          <Dropdown
            name="type-of-stock"
            options={[
              { value: TypeOfStockEnum.IN_MY_WAREHOUSE, label: translate("pq-ingestion.type-of-stock.in-my-warehouse") },
              { value: TypeOfStockEnum.ORDERED_IN_TRANSIT, label: translate("pq-ingestion.type-of-stock.ordered-in-transit") },
              { value: TypeOfStockEnum.ORDERED, label: translate("pq-ingestion.type-of-stock.ordered") },
              { value: TypeOfStockEnum.AVAILABLE_TO_ORDER, label: translate("pq-ingestion.type-of-stock.available-to-order") },
            ]}
            value={typeOfStock}
            onChange={(value: string) => {
              setTypeOfStock(value as TypeOfStockEnum);
              setError(null);
            }}
            placeholder={translate("pq-ingestion.type-of-stock")}
            useRelativePositioning
          />
        </Flex>

        {/* Step 3: Available Time Period */}
        <Flex className="flex-col gap-3 shrink-0">
          <Flex className="items-center gap-2">
            <Flex className="w-8 h-8 rounded-full bg-gray-300 items-center justify-center shrink-0">
              <Text className="text-gray-600 font-semibold text-sm">3</Text>
            </Flex>
            <Text className="text-base font-semibold text-gray-900 uppercase tracking-wide">
              {translate("pq-ingestion.available-time-period")} <span>*</span>
            </Text>
          </Flex>

          <Flex className="items-center gap-2">
            {/* Availability type dropdown */}
            <div className="flex-1 min-w-0">
              <Dropdown
                name="pq-type"
                options={[
                  { value: AvailabilityTypeEnum.ALWAYS_IN_STOCK, label: translate("single-product-offering.always-in-stock") },
                  { value: AvailabilityTypeEnum.UPCOMING_STOCK, label: translate("single-product-offering.upcoming-stock") },
                  { value: AvailabilityTypeEnum.IN_STOCK_NOW, label: translate("single-product-offering.in-stock-now") },
                ]}
                value={pqType}
                onChange={(value: string) => {
                  const newType = value as AvailabilityTypeEnum;
                  setPqType(newType);
                  setError(null);
                  if (newType === AvailabilityTypeEnum.IN_STOCK_NOW) {
                    setAvailableFromDate(new Date());
                  } else if (newType === AvailabilityTypeEnum.UPCOMING_STOCK) {
                    // Restore the user's previously entered date if it exists
                    setAvailableFromDate(upcomingStockDateRef.current);
                  } else {
                    setAvailableFromDate(null);
                  }
                }}
                useRelativePositioning
              />
            </div>

            {/* Date picker — only for date-based types */}
            {pqType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && (
              <div className="w-[28%] min-w-0 shrink-0">
                <DatePickerButton
                  value={availableFromDate || undefined}
                  onChange={(date) => {
                    setAvailableFromDate(date);
                    if (pqType === AvailabilityTypeEnum.UPCOMING_STOCK) {
                      upcomingStockDateRef.current = date ?? null;
                    }
                    setError(null);
                  }}
                  placeholder="dd.mm.yyyy"
                  classNames="w-full"
                  uniqueQueryId="pq-ingestion-availability-date"
                  isDisabled={pqType === AvailabilityTypeEnum.IN_STOCK_NOW}
                />
              </div>
            )}

            {/* Lead time — always visible, optional (sends 0 when empty) */}
            <div className="w-[28%] min-w-0 shrink-0">
              <NumberInput
                name="delivery-time"
                value={deliveryTimeInDays}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                    setDeliveryTimeInDays(val);
                  }
                }}
                placeholder={translate("single-product-offering.preparation-lead-time")}
                classNames="w-full"
              />
            </div>
          </Flex>
        </Flex>

        {/* Step 4: Instructions */}
        <Flex className="flex-col gap-3 shrink-0">
          <Flex className="items-center gap-2">
            <Flex className="w-8 h-8 rounded-full bg-gray-300 items-center justify-center shrink-0">
              <Text className="text-gray-600 font-semibold text-sm">4</Text>
            </Flex>
            <Text className="text-base font-semibold text-gray-900 uppercase tracking-wide">
              {translate("pq-ingestion.instructions")} <span>*</span>
            </Text>
          </Flex>

          <Flex className="flex-col gap-2">
            <Text className="text-sm font-medium text-gray-700">
              {translate("pq-ingestion.instructions-for-processing")}
            </Text>
            <TextArea
              value={additionalInput}
              onChange={setAdditionalInput}
              placeholder={translate("pq-ingestion.add-context-placeholder")}
              rows={2}
              isDisabled={!uploadResponse}
            />
          </Flex>

          {/* Example Instructions */}
          <Flex className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex-col gap-2">
            <Flex className="items-center gap-2">
              <Icon name={IconName.Alert} size={spacing[4]} color={colors.gray[600]} />
              <Text className="text-sm font-semibold text-gray-700">
                {translate("pq-ingestion.example-instructions")}:
              </Text>
            </Flex>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 pl-2">
              <li>{translate("pq-ingestion.example-1")}</li>
              <li>{translate("pq-ingestion.example-2")}</li>
              <li>{translate("pq-ingestion.example-3")}</li>
            </ul>
          </Flex>
        </Flex>
      </Flex>

      {/* Submit Button - Sticky at bottom */}
      <Flex className="border-gray-200 w-full">
        <Button
          buttonStyle={ButtonTypesEnum.primary}
          text={translate("pq-ingestion.submit-for-processing")}
          onClick={handleSubmitForProcessing}
          disabled={!uploadResponse || isSubmitting || !typeOfStock || !additionalInput.trim()}
          isLoading={isSubmitting}
          icon={<Icon name={IconName.Upload} size={spacing[4]} customStrokeWidth={2.5} color={colors.white} />}
          buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-3 px-6 w-full"
        />
      </Flex>
    </Modal>
  );
};

export default PQIngestionFileUploadModal;
